import { z } from 'zod';

// Validation générale
export const projectSchema = z.object({
  amount: z.number()
    .min(500, 'Le montant minimum est de 500 €')
    .max(35000, 'Le montant maximum est de 35 000 €'),
  
  project_type: z.enum(['travaux', 'besoin', 'auto', 'conso'], {
    errorMap: () => ({ message: 'Veuillez sélectionner un type de projet' })
  }),
  
  duration: z.string()
    .min(1, 'Veuillez sélectionner une durée')
});

// Validation des informations personnelles
export const personalInfoSchema = z.object({
  title: z.enum(['M', 'MME', 'MLE'], {
    errorMap: () => ({ message: 'Veuillez sélectionner un titre' })
  }),
  
  firstName: z.string()
    .min(2, 'Le prénom doit contenir au moins 2 caractères')
    .max(32, 'Le prénom ne peut pas dépasser 32 caractères')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le prénom contient des caractères non autorisés'),

  lastName: z.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(32, 'Le nom ne peut pas dépasser 32 caractères')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le nom contient des caractères non autorisés'),

  birthName: z.string()
    .optional()
    .or(z.literal('')),

  email: z.string()
    .email('Veuillez saisir un email valide')
    .min(1, 'L\'email est requis')
    .regex(/^[^+]+@[^+]+$/, 'Les alias email avec "+" ne sont pas acceptés'),
  
  phone: z.string()
    .regex(/^(?:(?:\+|00)33\s*|0)[67](?:[\s.-]*\d{2}){4}$/, 'Veuillez saisir un numéro de mobile valide (06 ou 07)')
    .min(1, 'Le téléphone mobile est requis'),
  
  birthDate: z.string()
    .min(1, 'La date de naissance est requise')
    .refine((date) => {
      const birthDate = new Date(date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age >= 18 && age <= 100;
    }, 'Vous devez avoir entre 18 et 100 ans'),

  nationality: z.string()
    .min(1, 'Veuillez sélectionner votre nationalité'),

  birthCountry: z.string()
    .min(1, 'Veuillez sélectionner votre pays de naissance'),

  birthCity: z.string().optional().or(z.literal('')),

  birthDepartment: z.string().optional().or(z.literal(''))
});

// Validation de l'adresse
export const addressSchema = z.object({
  country: z.enum(['FR', 'MC'], {
    errorMap: () => ({ message: 'Veuillez sélectionner un pays' })
  }),
  
  city: z.string()
    .min(2, 'La ville doit contenir au moins 2 caractères')
    .max(100, 'La ville ne peut pas dépasser 100 caractères')
    .regex(/[a-zA-ZÀ-ÿ]/, 'Veuillez saisir un nom de ville valide'),
  
  street: z.string()
    .min(5, 'L\'adresse doit contenir au moins 5 caractères')
    .max(38, 'L\'adresse ne peut pas dépasser 38 caractères'),
  
  postalCode: z.string()
    .regex(/^[0-9]{5}$/, 'Le code postal doit contenir exactement 5 chiffres')
});

// Validation de la situation familiale
export const familyStatusSchema = z.object({
  maritalStatus: z.enum(['C', 'D', 'K', 'M', 'P', 'S', 'V'], {
    errorMap: () => ({ message: 'Veuillez sélectionner votre situation maritale' })
  })
});


// Schémas composés pour validation complète
export const simulationStepSchema = z.object({
  amount: z.number()
    .min(500, 'Le montant minimum est de 500 €')
    .max(35000, 'Le montant maximum est de 35 000 €')
    .refine(val => val % 500 === 0, 'Le montant doit être un multiple de 500 €'),
  
  project_type: z.enum(['travaux', 'besoin', 'auto', 'conso'], {
    errorMap: () => ({ message: 'Veuillez sélectionner un type de projet' })
  })
});

export const durationStepSchema = z.object({
  duration: z.string()
    .min(1, 'Veuillez sélectionner une durée'),
  demandMaturity: z.string()
    .min(1, 'Veuillez sélectionner quand vous souhaitez recevoir les fonds')
});

export const personalStepSchema = personalInfoSchema.merge(
  z.object({
    professionCode: z.string().min(1, 'Veuillez sélectionner votre situation professionnelle'),
    contractTypeCode: z.string().optional().or(z.literal('')),
    contractEndDate: z.string().optional().or(z.literal('')),
    companyCity: z.string().optional().or(z.literal('')),
    monthlySalary: z.preprocess(
      (val) => (val === '' || val === null || val === undefined || val === 0 || (typeof val === 'number' && isNaN(val)) ? undefined : val),
      z.number()
        .min(1, 'Le salaire doit être supérieur à 0')
        .max(50000, 'Veuillez contacter notre service pour les salaires supérieurs à 50 000€')
        .optional()
    ),
  })
).superRefine((data, ctx) => {
  if (data.birthCountry === 'FR') {
    if (!data.birthCity || data.birthCity.length < 2 || data.birthDepartment === '99') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Veuillez sélectionner votre ville de naissance dans la liste', path: ['birthCity'] });
    }
    if (!data.birthDepartment || !/^([0-9]{2,3})$/.test(data.birthDepartment)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Le département est invalide', path: ['birthDepartment'] });
    }
  }
  if (['CDD', 'INT'].includes(data.contractTypeCode || '')) {
    if (!data.contractEndDate || data.contractEndDate.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'La date de fin de contrat est requise pour les CDD et Intérim', path: ['contractEndDate'] });
    } else {
      const endDate = new Date(data.contractEndDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      if (endDate <= today) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'La date de fin de contrat doit être dans le futur', path: ['contractEndDate'] });
      }
    }
  }
  // Salaire requis uniquement pour les actifs (professionCode <= 6300)
  const codeNum = parseInt((data as any).professionCode || '0', 10);
  if (codeNum > 0 && codeNum <= 6300) {
    const salary = (data as any).monthlySalary;
    if (salary === undefined || salary === null || (salary as number) < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Veuillez indiquer vos revenus mensuels', path: ['monthlySalary'] });
    }
  }

  // Ville employeur requise pour les codes 3300, 3800, 6300 (spec Cofidis)
  if ([3300, 3800, 6300].includes(codeNum)) {
    if (!(data as any).companyCity || String((data as any).companyCity).trim().length < 2) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'La ville de l\'employeur est requise', path: ['companyCity'] });
    }
  }
});

export const addressStepSchema = addressSchema;

export const completeFormSchema = projectSchema
  .merge(personalInfoSchema)
  .merge(addressSchema)
  .merge(familyStatusSchema);

// Fonction utilitaire pour valider une étape spécifique
export function validateStep(stepId: string, data: any): {
  success: boolean;
  errors: Record<string, string>;
} {
  let schema: z.ZodSchema;
  
  switch (stepId) {
    case 'simulation':
      schema = simulationStepSchema;
      break;
    case 'duration':
      schema = durationStepSchema;
      break;
    case 'personal_info':
      schema = personalStepSchema;
      break;
    case 'address':
      schema = addressStepSchema;
      break;
    default:
      return { success: true, errors: {} };
  }
  
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, errors: {} };
  }
  
  const errors: Record<string, string> = {};
  result.error.errors.forEach((error) => {
    const field = error.path[0] as string;
    errors[field] = error.message;
  });
  
  return { success: false, errors };
}

// Fonction pour valider un champ individuel
export function validateField(fieldName: string, value: any, stepId: string): string | null {
  const stepValidation = validateStep(stepId, { [fieldName]: value });
  return stepValidation.errors[fieldName] || null;
}

// Messages d'erreur personnalisés en français
export const errorMessages = {
  required: 'Ce champ est requis',
  email: 'Veuillez saisir un email valide',
  minLength: (min: number) => `Ce champ doit contenir au moins ${min} caractères`,
  maxLength: (max: number) => `Ce champ ne peut pas dépasser ${max} caractères`,
  min: (min: number) => `La valeur minimum est ${min}`,
  max: (max: number) => `La valeur maximum est ${max}`,
  pattern: 'Le format saisi n\'est pas valide'
};

// Type guard pour vérifier si un objet est une erreur Zod
export function isZodError(error: any): error is z.ZodError {
  return error instanceof z.ZodError;
}